import { Stat } from '@/components/ui/Stat';
import { Skeleton } from '@/components/ui/Skeleton';
import { useServiceHealth, useSystemMetrics } from '@/hooks/useAdminMonitoring';
import { Cpu, HardDrive, MemoryStick, Activity } from 'lucide-react';
import { ServiceStatusCard } from '../components/ServiceStatusCard';
import { DatabaseMetrics } from '../components/DatabaseMetrics';
import { RedisMetrics } from '../components/RedisMetrics';
import { MinioMetrics } from '../components/MinioMetrics';
import { ApiLatencyChart } from '../components/ApiLatencyChart';
import { ErrorRateChart } from '../components/ErrorRateChart';
import { CronJobsStatus } from '../components/CronJobsStatus';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useMemo } from 'react';

export function SystemHealthPage() {
  const { data: healthData, isLoading: healthLoading } = useServiceHealth();
  const { data: metricsData } = useSystemMetrics();

  // Health API returns { status, timestamp, services: { database: {...}, redis: {...}, minio: {...} } }
  const services = useMemo(() => {
    const servicesObj = healthData?.services;
    if (!servicesObj || typeof servicesObj !== 'object') return [];
    return Object.entries(servicesObj).map(([name, details]) => ({
      name,
      status: details?.status ?? 'unknown',
      latency: details?.latency ?? 0,
      details: details as Record<string, unknown>,
    }));
  }, [healthData]);

  // Metrics API returns { system: { uptime, memory: { rss, heapTotal, heapUsed }, ... }, database: { size, activeConnections }, timestamp }
  const system = metricsData?.system;
  const memoryUsed = system?.memory?.heapUsed;
  const memoryTotal = system?.memory?.heapTotal;
  const memoryPercent = memoryUsed && memoryTotal ? Math.round((memoryUsed / memoryTotal) * 100) : undefined;
  const dbConnections = metricsData?.database?.activeConnections;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">Monitoring</h1><p className="text-sm text-neutral-500">Santé de l'infrastructure</p></div>
        <div className="flex gap-2">
          <Link to="/monitoring/logs"><Button variant="outline" size="sm">Logs</Button></Link>
          <Link to="/monitoring/browser-pool"><Button variant="outline" size="sm">Browser Pool</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Statut global" value={healthData?.status ?? '--'} icon={<Cpu className="h-5 w-5" />} />
        <Stat label="Mémoire" value={memoryPercent != null ? `${memoryPercent}%` : '--'} icon={<MemoryStick className="h-5 w-5" />} />
        <Stat label="Connexions DB" value={dbConnections ?? '--'} icon={<HardDrive className="h-5 w-5" />} />
        <Stat label="Services" value={`${services.filter(s => s.status === 'healthy').length}/${services.length}`} icon={<Activity className="h-5 w-5" />} />
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-neutral-600 dark:text-neutral-300">Services</h2>
        {healthLoading ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4"><Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
        ) : (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {services.map((s) => <ServiceStatusCard key={s.name} service={s} />)}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ApiLatencyChart />
        <ErrorRateChart />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <DatabaseMetrics />
        <RedisMetrics />
        <MinioMetrics />
      </div>

      <CronJobsStatus />
    </div>
  );
}
